import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiFilm, FiCompass, FiPenTool } = FiIcons;

const Services = () => {
  const services = [
    {
      id: "01",
      icon: FiFilm,
      title: "Short-Form Mastery",
      desc: "Short-form edits built to stop the scroll and hold attention.",
      tags: ["Reels", "TikTok", "Shorts"]
    },
    {
      id: "02",
      icon: FiCompass,
      title: "Content Strategy",
      desc: "Clear content direction that turns output into consistency.",
      tags: ["Architecture", "Growth"]
    },
    {
      id: "03",
      icon: FiPenTool,
      title: "Visual Identity",
      desc: "A consistent visual language across every piece of content.",
      tags: ["Grading", "Graphics"]
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] }
    }
  };

  return (
    <section id="services" className="bg-white pt-10 md:pt-32 pb-10 md:pb-20 section-curve-up">
      <div className="max-w-[1400px] mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="flex flex-col md:flex-row justify-between items-start md:items-end mb-8 md:mb-24 gap-4 md:gap-6"
        >
          <div className="space-y-2 md:space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-8 md:w-10 h-[2px] bg-[#BEF264]" />
              <span className="text-[9px] md:text-[10px] font-black uppercase tracking-[0.4em] text-black/40">Expertise</span>
            </div>
            <h2 className="text-3xl md:text-7xl font-black text-black tracking-tighter leading-none uppercase">
              OUR <span className="text-black/10">CAPABILITIES</span>
            </h2>
          </div>
          <p className="text-black/70 max-w-sm font-medium leading-relaxed tracking-tight text-sm md:text-lg">
            We don't just edit. We architect digital presence for those who refuse to blend in.
          </p>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid md:grid-cols-3 gap-4 md:gap-8"
        >
          {services.map((service) => (
            <motion.div
              key={service.id}
              variants={itemVariants}
              whileHover={{ y: -12, transition: { duration: 0.4, ease: [0.16, 1, 0.3, 1] } }}
              className="group relative bg-white p-5 md:p-12 rounded-[1.75rem] md:rounded-[2.5rem] border border-black/[0.03] shadow-[0_4px_20px_-4px_rgba(0,0,0,0.02)] hover:shadow-[0_20px_40px_-12px_rgba(190,242,100,0.15)] transition-all duration-500 cursor-default overflow-hidden flex flex-col"
            >
              <div className="absolute top-0 left-0 w-full h-[3px] bg-[#BEF264] scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
              <div className="flex justify-between items-start mb-4 md:mb-6">
                <div className="flex flex-col">
                  <span className="text-[#BEF264] font-black text-4xl md:text-7xl tracking-tighter leading-none mb-1 md:mb-2">
                    {service.id}
                  </span>
                  <div className="w-6 md:w-16 h-px bg-black/10 group-hover:w-16 group-hover:bg-[#BEF264] transition-all duration-500" />
                </div>
                <div className="p-2.5 md:p-3 rounded-xl md:rounded-2xl bg-black/[0.02] group-hover:bg-[#BEF264]/10 transition-colors duration-500">
                  <SafeIcon icon={service.icon} className="w-4 h-4 md:w-6 md:h-6 text-black/20 group-hover:text-black transition-colors stroke-[1.5px]" />
                </div>
              </div>
              <h3 className="text-lg md:text-3xl font-black text-black mb-2 md:mb-4 uppercase tracking-tighter leading-none">
                {service.title}
              </h3>
              <p className="text-black/50 group-hover:text-black/70 mb-4 md:mb-10 font-medium leading-relaxed tracking-tight text-[12px] md:text-base transition-colors duration-500">
                {service.desc}
              </p>
              <div className="flex flex-wrap gap-2 mt-auto">
                {service.tags.map(tag => (
                  <span
                    key={tag}
                    className="px-3 md:px-4 py-1 rounded-full border border-black/[0.05] group-hover:border-[#BEF264] text-[8px] md:text-[9px] uppercase font-black tracking-[0.2em] text-black/30 group-hover:text-black transition-all duration-500"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          initial={{ scaleX: 0 }}
          whileInView={{ scaleX: 1 }}
          transition={{ delay: 0.5, duration: 1 }}
          viewport={{ once: true }}
          className="h-px bg-black/[0.05] w-full mt-10 md:mt-16"
        />
      </div>
    </section>
  );
};

export default Services;